import type { AavoDialogProps } from "src/components/common/dialogs/AavoDialog.tsx";
import React, { useCallback, useEffect } from "react";
import { DataDirtyStateChangeHandler } from "src/utils/dataDirtyStateChangeHandler.ts";
import { useContextOrThrow } from "src/utils/useContextOrThrow.tsx";
import type { AgentUiScopeContext } from "src/components/views/ai/assistant/uiContext/agentUiNodeTypes.ts";
import { useCurrentAgentUiScopeId } from "src/components/views/ai/assistant/uiContext/AgentUiContext.ts";

export const GenericDialogContext = React.createContext<GenericDialogContextValue | undefined>(undefined);

export const useGenericDialog = (): GenericDialogContextValue => {
	const context = useContextOrThrow(GenericDialogContext);
	const agentUiParentScopeId = useCurrentAgentUiScopeId();

	const openDialog = useCallback<OpenGenericDialogFunc>(
		(propsProvider) => context.openDialog(propsProvider, { agentUiParentScopeId }),
		[agentUiParentScopeId, context],
	);

	return {
		...context,
		openDialog,
	};
};

export const useCloseCallbackForActiveDialog = (callback: () => Promise<unknown>) => {
	const { registerCloseCallbackForActiveDialog } = useGenericDialog();
	useEffect(() => {
		return registerCloseCallbackForActiveDialog(callback);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
};

/** Calls [callback] before the active dialog checks for unsaved changes, so content can refresh its dirty state. */
export const useBeforeCloseCallbackForActiveDialog = (callback: () => Promise<unknown>) => {
	const { registerBeforeCloseCallbackForActiveDialog } = useGenericDialog();
	useEffect(() => {
		return registerBeforeCloseCallbackForActiveDialog(callback);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
};

export interface GenericDialogContextValue {
	openDialog: OpenGenericDialogFunc;
	registerCloseCallbackForActiveDialog: (callback: () => Promise<unknown>) => () => void;
	registerBeforeCloseCallbackForActiveDialog: (callback: () => Promise<unknown>) => () => void;
}

export type OpenGenericDialogFunc = (
	propsProvider: GenericDialogProps | GenericDialogPropsProvider,
	options?: OpenGenericDialogOptions,
) => void;

export interface OpenGenericDialogOptions {
	agentUiParentScopeId?: string;
}

export type GenericDialogPropsProvider = (params: {
	closeDialog: CloseDialogFunc;
	onContentEdited: () => void;
	onDataDirtyStateChanged: DataDirtyStateChangeHandler;
}) => GenericDialogProps;

export type CloseDialogFunc = (params?: CloseDialogParams) => Promise<void>;

export interface CloseDialogParams {
	confirmIfEdited?: boolean;
}

export interface GenericDialogProps extends Omit<AavoDialogProps, "onClose" | "children" | "content"> {
	/** Refresh dirty state before the standard unsaved-change confirmation. */
	onBeforeClose?: () => void | Promise<void>;
	onClose?: () => unknown | Promise<unknown>;
	content: React.ReactNode;
	agentContext: AgentUiScopeContext;
}
